import { storableError } from '../../util/errors';
import { adminListReferrals } from '../../util/api';
import { parse } from '../../util/urlHelpers';

// ================ Action types ================ //
export const FETCH_REFERRALS_REQUEST = 'app/AdminReferralsPage/FETCH_REFERRALS_REQUEST';
export const FETCH_REFERRALS_SUCCESS = 'app/AdminReferralsPage/FETCH_REFERRALS_SUCCESS';
export const FETCH_REFERRALS_ERROR = 'app/AdminReferralsPage/FETCH_REFERRALS_ERROR';

// ================ Reducer ================ //
const emptyPage = { items: [], pagination: { totalItems: 0, totalPages: 0, page: 1, perPage: 20 } };
const initialState = {
  // Caches per status to avoid tab data mixups
  byStatus: {
    all: { ...emptyPage },
    pending: { ...emptyPage },
    signed_up: { ...emptyPage },
    completed: { ...emptyPage },
    credited: { ...emptyPage },
  },
  counts: { all: 0, pending: 0, signed_up: 0, completed: 0, credited: 0 },
  loading: false,
  error: null,
  pagination: { totalItems: 0, totalPages: 0, page: 1, perPage: 10 },
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_REFERRALS_REQUEST:
      return { ...state, loading: true, error: null };
    case FETCH_REFERRALS_SUCCESS: {
      const { items = [], pagination = emptyPage.pagination, status = 'all', counts = {} } = payload || {};
      const key = status || 'all';
      return {
        ...state,
        loading: false,
        error: null,
        pagination,
        byStatus: {
          ...state.byStatus,
          [key]: { items, pagination },
        },
        counts: { ...state.counts, ...counts },
      };
    }
    case FETCH_REFERRALS_ERROR:
      return { ...state, loading: false, error: payload };
    default:
      return state;
  }
}

// ================ Action creators ================ //
export const fetchReferralsRequest = () => ({ type: FETCH_REFERRALS_REQUEST });
export const fetchReferralsSuccess = payload => ({ type: FETCH_REFERRALS_SUCCESS, payload });
export const fetchReferralsError = error => ({ type: FETCH_REFERRALS_ERROR, payload: error });

// ================ Thunks ================ //
export const fetchReferrals = ({ page = 1, perPage = 10, status } = {}) => async (dispatch, getState) => {
  dispatch(fetchReferralsRequest());
  try {
    const currentUser = getState()?.user?.currentUser;
    const currentUserId = currentUser?.id?.uuid;
    if (!currentUserId) throw new Error('User not authenticated');

    const body = { page, perPage, currentUserId };
    if (status && status !== 'all') body.status = status;

    const response = await adminListReferrals(body);
    const data = response || {};
    const items = Array.isArray(data?.data) ? data.data : [];
    const counts = data?.counts || undefined;
    const statusKey = status || 'all';
    let pagination = data?.pagination;
    if (pagination) {
      const p = pagination.page ?? page;
      const pp = pagination.perPage ?? perPage;
      const totalItems =
        (pagination.totalItems ?? pagination.total) ?? (typeof (counts && counts[statusKey]) === 'number' ? counts[statusKey] : 0);
      const totalPages = pagination.totalPages ?? (totalItems ? Math.max(1, Math.ceil(totalItems / pp)) : 1);
      pagination = { page: p, perPage: pp, totalItems, totalPages };
    } else {
      const ti = typeof (counts && counts[statusKey]) === 'number' ? counts[statusKey] : 0;
      const tp = ti ? Math.max(1, Math.ceil(ti / perPage)) : 1;
      pagination = { page, perPage, totalItems: ti, totalPages: tp };
    }

    dispatch(fetchReferralsSuccess({ items, pagination, status: status || 'all', counts }));
  } catch (e) {
    const err = storableError(e);
    dispatch(fetchReferralsError(err));
  }
};

// ================ Page Data Loading ================ //
export const loadData = (params, search) => (dispatch, getState, sdk) => {
  const queryParams = parse(search);
  const page = Number(queryParams?.page || 1);
  const status = String(queryParams?.status || 'all');
  return dispatch(fetchReferrals({ page, perPage: 10, status }));
};
